import { useEffect, useState, type ChangeEvent, type FunctionComponent } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import toUpper from 'lodash/toUpper';
import { useForm } from 'react-hook-form';
import {
  COUNTRY_SELECTION,
  formatPhoneNumber,
  getStatesByCountryId,
  handleValidation,
  startCase,
  validateCharacterLimit,
} from '@fff-web/fff-utilities';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Input from '../Input';
import Select from '../Select';
import * as styles from './AddressForm.module.scss';

const UNITED_KINGDOM_ID = '76';
const CHARACTER_LIMIT = 31;
const ADDRESS_CHARACTER_LIMIT = 30;
const PHONE_CHARACTER_LIMIT = 18;
const PHONE_MASK_GB = '+44 __ ____ ____';
const PHONE_MASK_US_CA = '+1 (___) ___-____';

interface AddressFormProps {
  firstName: string;
  lastName: string;
  phone: string;
  address1: string;
  address2?: string;
  city: string;
  region: string;
  zipcode: string;
  country: string;
  isDefault?: boolean;
  submitButtonName: string;
  onSubmit: (formData: unknown) => void;
  cancelButtonName: string;
  onCancel: () => void;
  showPhoneNumber?: boolean;
  getStateList: (list: Array<unknown>) => void;
}

const AddressForm: FunctionComponent<AddressFormProps> = ({
  firstName,
  lastName,
  phone,
  address1,
  address2,
  city,
  region,
  zipcode,
  country,
  isDefault,
  submitButtonName,
  onSubmit,
  cancelButtonName,
  onCancel,
  showPhoneNumber,
  getStateList,
}) => {
  const defaultValues: Partial<AddressFormProps> = {
    firstName: startCase(toLower(firstName)),
    lastName: startCase(toLower(lastName)),
    phone: formatPhoneNumber(phone),
    address1: startCase(toLower(address1)),
    address2: startCase(toLower(address2)),
    city: startCase(toLower(city)),
    region: toUpper(region),
    country,
    zipcode,
    isDefault: false,
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  const [stateList, setStateList] = useState<{ id: string }[]>([]);
  const countryId = watch('country');
  const unitedKingdomSelected = countryId === UNITED_KINGDOM_ID;
  const phoneMask = unitedKingdomSelected ? PHONE_MASK_GB : PHONE_MASK_US_CA;

  const getRegions = () => {
    getStatesByCountryId(countryId).then((listOfStates: []) => {
      setStateList(listOfStates);
      getStateList(listOfStates);
      find(listOfStates, (st: { id: string }) => {
        const code = get(st, ['stateCode']);
        if (toUpper(code) === toUpper(region)) {
          setValue('region', st.id);
        }
      });
    });
  };

  const checkPrimaryAddress = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('isDefault', event.target.checked);
  };

  useEffect(() => {
    if (Number(countryId) > 0) {
      getRegions();
    }
  }, [countryId]);

  return (
    <form className={styles.editForm} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.editInnerContainer}>
        <div className={styles.inputContainer}>
          <Input
            style={{ width: '100%' }}
            label="First Name"
            datatestid="first-name-address-fld"
            {...register('firstName', {
              required: 'This field is required.',
              pattern: {
                value: /^[A-ZÀ-ÿa-z\\s,. '\\pL-]+$/,
                message: 'Please enter a valid first name.',
              },
              validate: (value) =>
                handleValidation(value, 'firstName', setError, clearErrors, validateCharacterLimit),
            })}
            error={errors.firstName?.message}
            characterLimit={CHARACTER_LIMIT}
          />
        </div>
        <div className={styles.inputContainer}>
          <Input
            style={{ width: '100%' }}
            label="Last Name"
            datatestid="last-name-address-fld"
            {...register('lastName', {
              required: 'This field is required.',
              pattern: {
                value: /^[A-ZÀ-ÿa-z\\s,. '\\pL-]+$/,
                message: 'Please enter a valid last name.',
              },
              validate: (value) =>
                handleValidation(value, 'lastName', setError, clearErrors, validateCharacterLimit),
            })}
            error={errors.lastName?.message}
            characterLimit={CHARACTER_LIMIT}
          />
        </div>
        <div className={styles.inputContainer}>
          <Input
            style={{ width: '100%' }}
            label="Address"
            datatestid="address-fld"
            {...register('address1', {
              required: 'Please enter a valid address.',
              validate: (value) =>
                handleValidation(value, 'address1', setError, clearErrors, validateCharacterLimit),
            })}
            error={errors.address1?.message}
            characterLimit={ADDRESS_CHARACTER_LIMIT}
          />
        </div>
        <div className={styles.inputContainer}>
          <Input
            style={{ width: '100%' }}
            label="Apartment / Suite / Unit etc. (optional)"
            datatestid="second-address-fld"
            {...register('address2')}
            characterLimit={CHARACTER_LIMIT}
          />
        </div>
        <div className={styles.inputContainer}>
          <Input
            style={{ width: '100%' }}
            label="City / Town"
            datatestid="city-address-fld"
            {...register('city', {
              required: 'Please enter a valid city.',
              validate: (value) =>
                handleValidation(value, 'city', setError, clearErrors, validateCharacterLimit),
            })}
            error={errors.city?.message}
            characterLimit={CHARACTER_LIMIT}
          />
        </div>
        <div className={styles.inputContainer}>
          <Select
            style={{ width: '100%' }}
            label="State"
            selectLabel="Select State"
            list={stateList}
            {...register('region', {
              required: !unitedKingdomSelected,
            })}
            disabled={unitedKingdomSelected}
            value={watch('region')}
            error={
              !unitedKingdomSelected && errors.region ? 'Please enter a valid state.' : undefined
            }
          />
        </div>
        <div className={styles.inputContainer}>
          <Select
            style={{ width: '100%' }}
            label="Country"
            selectLabel="Select Country"
            list={Object.values(COUNTRY_SELECTION)}
            {...register('country', {
              required: true,
            })}
            value={watch('country')}
            error={errors.country && 'Please enter a valid country.'}
          />
        </div>
        <div className={styles.inputContainer}>
          <Input
            style={{ width: '100%' }}
            label="Zipcode"
            datatestid="zipcode-address-fld"
            {...register('zipcode', {
              required: 'Please enter a valid zipcode.',
            })}
            error={errors.zipcode && errors.zipcode.message}
          />
        </div>
        {showPhoneNumber && (
          <div className={styles.inputContainer}>
            <Input
              style={{ width: '100%' }}
              label="Phone Number"
              datatestid="phone-number-address-fld"
              {...register('phone', {
                required: 'Please enter a valid phone number.',
                validate: (value) =>
                  handleValidation(value, 'phone', setError, clearErrors, validateCharacterLimit),
              })}
              error={errors.phone?.message}
              characterLimit={PHONE_CHARACTER_LIMIT}
              inputMask={phoneMask}
              type="phone"
            />
          </div>
        )}
      </div>
      {!isDefault && (
        <div>
          <Checkbox
            {...register('isDefault')}
            onChange={(e) => checkPrimaryAddress(e)}
            label="Make primary address"
            data-testid="make-primary-fld"
          />
        </div>
      )}
      <div className={styles.buttonsContainer}>
        <Button onClick={onCancel} variation="black-outline">
          {cancelButtonName}
        </Button>
        <Button type="submit" variation="black">
          {submitButtonName}
        </Button>
      </div>
    </form>
  );
};

export default AddressForm;
